// Reordering a vertical list by a handle (R-U20; the look of the editor's handle, DESIGN.md «Редактор»): the item follows the pointer,
// the others make way with a slide, the new order is given on release. Arrow keys on the handle move an item one step.

import { useEffect, useRef, useState, type CSSProperties } from 'react';

interface Drag { key: string; dy: number; order: string[]; tops: Map<string, number>; heights: Map<string, number>; gap: number; first: number }

/** Where a list of the given heights puts each item when laid out in `order` from `first` with `gap` between items. */
export function layout(order: string[], heights: Map<string, number>, first: number, gap: number): Map<string, number> {
  const at = new Map<string, number>();
  let y = first;
  for (const k of order) { at.set(k, y); y += (heights.get(k) ?? 0) + gap; }
  return at;
}

/** The order while dragging `key` by `dy`: the item takes the place its centre has reached. */
export function orderAt(keys: string[], key: string, dy: number, tops: Map<string, number>, heights: Map<string, number>): string[] {
  const centre = (tops.get(key) ?? 0) + dy + (heights.get(key) ?? 0) / 2;
  const rest = keys.filter((k) => k !== key);
  const index = rest.filter((k) => (tops.get(k) ?? 0) + (heights.get(k) ?? 0) / 2 < centre).length;
  return [...rest.slice(0, index), key, ...rest.slice(index)];
}

export function useSortable(keys: string[], onMove: (order: string[]) => void) {
  const els = useRef(new Map<string, HTMLElement>());
  const [drag, setDrag] = useState<Drag | null>(null);
  const done = (order: string[]) => { if (order.join() !== keys.join()) onMove(order); };
  // A drag that never ends here (the card closed under it) takes its window listeners with the list.
  const stop = useRef<(() => void) | null>(null);
  useEffect(() => () => stop.current?.(), []);

  const start = (key: string, e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const tops = new Map<string, number>(), heights = new Map<string, number>();
    for (const k of keys) { const r = els.current.get(k)?.getBoundingClientRect(); if (r) { tops.set(k, r.top); heights.set(k, r.height); } }
    const [a, b] = keys;
    const gap = a && b ? (tops.get(b)! - tops.get(a)! - heights.get(a)!) : 0;
    const startY = e.clientY;
    let cur: Drag = { key, dy: 0, order: keys, tops, heights, gap, first: tops.get(keys[0]!) ?? 0 };
    setDrag(cur);
    const move = (ev: PointerEvent) => { cur = { ...cur, dy: ev.clientY - startY, order: orderAt(keys, key, ev.clientY - startY, tops, heights) }; setDrag(cur); };
    const off = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up); stop.current = null; };
    const up = () => { off(); setDrag(null); done(cur.order); };
    stop.current?.();
    stop.current = off;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const step = (key: string, e: React.KeyboardEvent) => {
    const by = e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : 0;
    const i = keys.indexOf(key), j = i + by;
    if (!by || j < 0 || j >= keys.length) return;
    e.preventDefault();
    const next = [...keys];
    [next[i], next[j]] = [next[j]!, next[i]!];
    done(next);
  };

  const target = drag ? layout(drag.order, drag.heights, drag.first, drag.gap) : null;
  return {
    dragging: drag?.key ?? null,
    /** The item's element, measured when a drag starts. */
    ref: (key: string) => (el: HTMLElement | null) => { if (el) els.current.set(key, el); else els.current.delete(key); },
    /** The dragged item follows the pointer; the others slide to their places in the order being made. */
    style: (key: string): CSSProperties | undefined => {
      if (!drag || !target) return undefined;
      if (key === drag.key) return { transform: `translateY(${drag.dy}px)`, transition: 'none', position: 'relative', zIndex: 1 };
      return { transform: `translateY(${(target.get(key) ?? 0) - (drag.tops.get(key) ?? 0)}px)`, transition: 'transform var(--dur) var(--ease-out)' };
    },
    /** Props of the handle. */
    handle: (key: string) => ({ onPointerDown: (e: React.PointerEvent) => start(key, e), onKeyDown: (e: React.KeyboardEvent) => step(key, e) }),
  };
}
